/* Editorial interactions only; Horizon and Shopify own all purchasing state. */
(() => {
  'use strict';
  const instances = new Map();
  function carousel(element) {
    const lifecycle=new AbortController(), {signal}=lifecycle;
    const track=element.querySelector('.hero-track'), slides=[...track.children];
    const buttons=[...element.querySelectorAll('[data-slide]')];
    const rotation=element.querySelector('.hero-rotation');
    const status=element.querySelector('.hero-status');
    const motion=matchMedia('(prefers-reduced-motion: reduce)');
    let current=0, paused=motion.matches, keyboardFocus=false, visible=false, gesture=null, suppressClickUntil=0, timer;
    function schedule(){clearTimeout(timer);if(!paused&&!keyboardFocus&&!gesture&&visible&&!document.hidden)timer=setTimeout(()=>show(current+1),3500);}
    function pause(value){paused=value;rotation.textContent=paused?'Play':'Pause';rotation.setAttribute('aria-label',paused?'Play slideshow':'Pause slideshow');schedule();}
    function show(index,manual=false){
      current=(index+slides.length)%slides.length;
      track.style.transform=`translateX(-${current*100}%)`;
      slides.forEach((slide,i)=>{slide.hidden=false;slide.inert=i!==current;slide.setAttribute('aria-hidden',String(i!==current));buttons[i].setAttribute('aria-pressed',String(i===current));});
      if(manual)status.textContent=slides[current].getAttribute('aria-label');schedule();
    }
    element.querySelector('.hero-controls').hidden=false;show(0);pause(paused);
    buttons.forEach(b=>b.addEventListener('click',()=>show(Number(b.dataset.slide),true),{signal}));
    element.querySelector('.hero-previous').addEventListener('click',()=>show(current-1,true),{signal});
    element.querySelector('.hero-next').addEventListener('click',()=>show(current+1,true),{signal});
    rotation.addEventListener('click',()=>pause(!paused),{signal});
    // Keyboard readers can explore a slide without it moving out from under focus.
    // Mouse hover and taps no longer stop automatic rotation indefinitely.
    element.addEventListener('focusin',e=>{keyboardFocus=e.target!==rotation&&e.target.matches(':focus-visible');schedule();},{signal});
    element.addEventListener('focusout',e=>{if(!element.contains(e.relatedTarget)){keyboardFocus=false;schedule();}},{signal});
    const viewport=element.querySelector('.hero-viewport');
    viewport.addEventListener('pointerdown',e=>{
      if(e.pointerType==='mouse'||!e.isPrimary)return;
      gesture={id:e.pointerId,x:e.clientX,y:e.clientY};keyboardFocus=false;schedule();
    },{signal});
    viewport.addEventListener('pointermove',e=>{
      if(gesture?.id===e.pointerId&&Math.abs(e.clientX-gesture.x)>12&&Math.abs(e.clientX-gesture.x)>Math.abs(e.clientY-gesture.y)*1.3)viewport.setPointerCapture(e.pointerId);
    },{signal});
    viewport.addEventListener('pointerup',e=>{
      if(!gesture||gesture.id!==e.pointerId)return;
      const dx=e.clientX-gesture.x,dy=e.clientY-gesture.y;
      gesture=null;
      if(Math.abs(dx)>=45&&Math.abs(dx)>Math.abs(dy)*1.3){suppressClickUntil=e.timeStamp+500;show(current+(dx<0?1:-1),true);}
      else schedule();
    },{signal});
    viewport.addEventListener('pointercancel',()=>{gesture=null;schedule();},{signal});
    viewport.addEventListener('click',e=>{if(e.timeStamp<suppressClickUntil){e.preventDefault();e.stopPropagation();}},{capture:true,signal});
    document.addEventListener('visibilitychange',schedule,{signal});
    motion.addEventListener('change',e=>{if(e.matches)pause(true);},{signal});
    const observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;schedule();},{threshold:.25});observer.observe(element);
    return()=>{lifecycle.abort();observer.disconnect();clearTimeout(timer);};
  }
  function header(element){
    const lifecycle=new AbortController(),{signal}=lifecycle;
    const button=element.querySelector('.mobile-menu'),nav=element.querySelector('nav');
    element.dataset.riseEnhanced='true';
    function toggle(open){button.setAttribute('aria-expanded',String(open));button.textContent=open?'Close':'Menu';nav.classList.toggle('is-open',open);}
    button.addEventListener('click',()=>toggle(button.getAttribute('aria-expanded')!=='true'),{signal});
    nav.addEventListener('click',e=>{if(e.target.closest('a'))toggle(false);},{signal});
    element.addEventListener('keydown',e=>{if(e.key==='Escape'){toggle(false);button.focus();}},{signal});
    return()=>lifecycle.abort();
  }
  function risers(element){
    const lifecycle=new AbortController(),{signal}=lifecycle;
    const gallery=element.querySelector('.rise-riser-gallery');
    if(!gallery)return()=>lifecycle.abort();
    const slides=[...gallery.querySelectorAll('[data-riser-slide]')];
    let current=0;
    function show(index){
      current=(index+slides.length)%slides.length;
      slides.forEach((slide,i)=>{slide.hidden=i!==current;});
      const data=slides[current].dataset;
      gallery.querySelector('[data-riser-position]').textContent=`${data.finish} · ${current+1} / ${slides.length}`;
      const link=element.querySelector('[data-rise-riser-link]');
      if(link){if(data.productUrl){link.href=data.productUrl;link.removeAttribute('aria-disabled');}else{link.removeAttribute('href');link.setAttribute('aria-disabled','true');}}
    }
    gallery.querySelector('.rise-riser-gallery-controls').hidden=false;
    gallery.querySelector('[data-riser-previous]').addEventListener('click',()=>show(current-1),{signal});
    gallery.querySelector('[data-riser-next]').addEventListener('click',()=>show(current+1),{signal});
    gallery.addEventListener('keydown',e=>{
      if(e.key==='ArrowLeft'||e.key==='ArrowRight'){e.preventDefault();show(current+(e.key==='ArrowRight'?1:-1));}
    },{signal});
    show(0);
    return()=>lifecycle.abort();
  }
  function mount(){
    for(const [selector,init]of [['.hero-carousel',carousel],['[data-rise-header]',header],['[data-rise-section="risers"]',risers]]){
      document.querySelectorAll(selector).forEach(el=>{if(!instances.has(el))instances.set(el,init(el));});
    }
  }
  document.addEventListener('shopify:section:load',mount);
  document.addEventListener('shopify:section:unload',e=>{for(const[el,destroy]of instances){if(e.target.contains(el)){destroy();instances.delete(el);}}});
  // Native Horizon forms own purchases and errors. Only confirmed additions show this notice.
  // Read the authoritative total: the add event's itemCount is only the quantity just added.
  let cartReadVersion=0;
  async function refreshBagCount(){
    const url=document.querySelector('[data-rise-cart-url]')?.getAttribute('href');
    if(!url)return;
    const version=++cartReadVersion;
    try{
      const response=await fetch(`${url}.js`,{headers:{Accept:'application/json'},cache:'no-store'});
      if(!response.ok)return;
      const cart=await response.json();
      if(version!==cartReadVersion||!Number.isInteger(cart.item_count))return;
      document.querySelectorAll('[data-rise-cart-count]').forEach(el=>{
        el.textContent=cart.item_count?String(cart.item_count):'';
        el.closest('a')?.setAttribute('aria-label',`Shopping bag, ${cart.item_count} ${cart.item_count===1?'item':'items'}`);
      });
    }catch{/* Keep the last confirmed count; a count refresh must never interrupt shopping. */}
  }
  document.addEventListener('cart:update',e=>{
    const data=e.detail?.data;
    refreshBagCount();
    if(data?.source==='product-form-component'&&!data.didError){
      const notice=document.querySelector('[data-rise-cart-notice]');
      if(notice)notice.hidden=false;
    }
  });
  document.addEventListener('click',e=>{
    if(e.target.closest('[data-rise-dismiss-notice]'))document.querySelector('[data-rise-cart-notice]').hidden=true;
  });
  document.addEventListener('cart:error',()=>{
    const notice=document.querySelector('[data-rise-cart-notice]');
    if(notice)notice.hidden=true;
  });
  window.addEventListener('pageshow',e=>{if(e.persisted)refreshBagCount();});
  mount();
})();
